import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSocket } from '../context/SocketContext';
import './ChatDetail.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const ChatDetail = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const socket = useSocket();
  
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [otherUserName, setOtherUserName] = useState(location.state?.userName || 'User');
  const messagesEndRef = useRef(null);
  const conversationIdRef = useRef(null);
  const textareaRef = useRef(null);

  const getCurrentUserId = () => {
    const uid = user?._id || user?.id;
    return uid ? uid.toString() : null;
  };

  const fetchMessagesFromAPI = useCallback(async () => {
    if (!token || !id) return;
    try {
      const response = await fetch(`${API_BASE}/messages/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        const normalized = data.messages.map(msg => ({
          ...msg,
          senderId: typeof msg.senderId === 'object' ? msg.senderId?._id?.toString() || msg.senderId?.toString() : msg.senderId?.toString(),
          receiverId: typeof msg.receiverId === 'object' ? msg.receiverId?._id?.toString() || msg.receiverId?.toString() : msg.receiverId?.toString()
        }));
        setMessages(normalized);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [id, token]);

  useEffect(() => {
    setMessages([]);
    setLoading(true);
    fetchMessagesFromAPI();
  }, [fetchMessagesFromAPI]);

  useEffect(() => {
    const myId = getCurrentUserId();
    if (myId && id) {
      conversationIdRef.current = [myId, id].sort().join('-');
    }
  }, [id, user]);

  useEffect(() => {
    if (!socket) return;
    const handleNewMessage = (data) => {
      const msg = data.message || data;
      const convId = data.conversationId || msg?.conversationId;
      if (convId === conversationIdRef.current) {
        const normalizedMsg = {
          ...msg,
          _id: msg._id || Date.now().toString(),
          senderId: msg.senderId?.toString?.() || msg.senderId,
          receiverId: msg.receiverId?.toString?.() || msg.receiverId,
          message: msg.message || '',
          senderName: msg.senderName || data.senderName || '',
          createdAt: msg.createdAt || new Date().toISOString(),
          isRead: msg.isRead || false
        };
        setMessages(prev => {
          if (prev.some(m => m._id === normalizedMsg._id)) return prev;
          return [...prev, normalizedMsg];
        });
      }
    };
    socket.on('new_message', handleNewMessage);
    return () => { socket.off('new_message', handleNewMessage); };
  }, [socket, id]);

  useEffect(() => {
    setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
  }, [messages]);

  useEffect(() => {
    const fetchUserName = async () => {
      if (!otherUserName || otherUserName === 'User') {
        try {
          const res = await fetch(`${API_BASE}/users/${id}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const data = await res.json();
          if (data.success && data.user) {
            setOtherUserName(data.user.name || data.user.company || 'User');
          }
        } catch (err) {
          console.error(err);
        }
      }
    };
    if (id && token) fetchUserName();
  }, [id, token, otherUserName]);

  // Auto-focus the textarea when component mounts
  useEffect(() => {
    if (textareaRef.current && !loading) {
      textareaRef.current.focus();
    }
  }, [loading]);

  const handleSendMessage = async () => {
    if (!newMessage.trim() || sending) return;
    const messageText = newMessage.trim();
    setNewMessage('');
    setSending(true);
    try {
      const response = await fetch(`${API_BASE}/messages/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ receiverId: id, message: messageText })
      });
      const data = await response.json();
      if (data.success && data.message) {
        const sentMsg = {
          ...data.message,
          senderId: data.message.senderId?.toString?.() || data.message.senderId,
          receiverId: data.message.receiverId?.toString?.() || data.message.receiverId
        };
        setMessages(prev => {
          if (prev.some(m => m._id === sentMsg._id)) return prev;
          return [...prev, sentMsg];
        });
      }
      // Clear the message and force focus back to textarea
      setNewMessage('');
      // Use setTimeout to ensure focus after state update and DOM re-render
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
        }
      }, 10);
    } catch (error) {
      console.error('Send message error:', error);
      setNewMessage(messageText);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (timestamp) => timestamp ? new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (date.toDateString() === today.toDateString()) return 'Today';
    if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return date.toLocaleDateString();
  };

  const groupedMessages = () => {
    const groups = [];
    let currentDate = null;
    messages.forEach(msg => {
      const msgDate = formatDate(msg.createdAt);
      if (msgDate !== currentDate) {
        currentDate = msgDate;
        groups.push({ date: currentDate, messages: [msg] });
      } else {
        groups[groups.length - 1].messages.push(msg);
      }
    });
    return groups;
  };

  if (loading) return <div className="loader-container"><i className="fa-solid fa-spinner fa-spin"></i></div>;

  const myId = getCurrentUserId();

  return (
    <div className="chat-wrapper">
      <div className="chat-header">
        <button className="chat-btn-back" onClick={() => navigate(-1)}>
          <i className="fa-solid fa-arrow-left"></i>
        </button>
        <div className="chat-user">
          <div className="chat-avatar">{otherUserName?.charAt(0).toUpperCase()}</div>
          <div className="chat-user-info">
            <span className="chat-user-name">{otherUserName}</span>
            <span className="chat-user-status">
              <span className="status-dot" style={{ background: socket?.connected ? '#18cb96' : '#666666' }}></span>
              {socket?.connected ? 'Online' : 'Connecting...'}
            </span>
          </div>
        </div>
      </div>

      <div className="chat-body">
        {messages.length === 0 && !loading && (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666666' }}>
            <i className="fa-regular fa-comments" style={{ fontSize: '40px', marginBottom: '16px' }}></i>
            <p style={{ fontSize: '13px' }}>No messages yet. Start the conversation!</p>
          </div>
        )}
        
        {groupedMessages().map((group, idx) => (
          <div key={idx}>
            <div className="chat-date-divider"><span>{group.date}</span></div>
            {group.messages.map((msg, msgIdx) => {
              const senderId = msg.senderId?.toString?.() || msg.senderId;
              const isSentByMe = myId && senderId && myId === senderId;
              
              return (
                <div key={msg._id || msgIdx} className={`chat-row ${isSentByMe ? 'sent' : 'received'}`}>
                  <div className="chat-bubble">
                    <p className="chat-text">{msg.message}</p>
                    <div className="chat-meta">
                      <span>{formatTime(msg.createdAt)}</span>
                      {isSentByMe && <span>{msg.isRead ? <i className="fa-solid fa-check-double"></i> : <i className="fa-solid fa-check"></i>}</span>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      <div className="chat-footer">
        <textarea 
          ref={textareaRef}
          className="chat-input"
          value={newMessage} 
          onChange={(e) => setNewMessage(e.target.value)} 
          onKeyDown={handleKeyDown} 
          placeholder="Type your message... (Press Enter to send)" 
          rows="1" 
          disabled={sending} 
        />
        <button className="chat-btn-send" onClick={handleSendMessage} disabled={sending || !newMessage.trim()}>
          <i className="fa-solid fa-paper-plane"></i>
        </button>
      </div>
    </div>
  );
};

export default ChatDetail;